import { z } from "zod";
import { BeneficiaryRelationshipsEnumSchema } from "state/RootSchema";

export type RecoveryStep = z.infer<typeof RecoveryStep>;
export const RecoveryStep = z.union([
    z.literal("FETCH_PAYMENT"),
    z.literal("FAST_SUBMIT"),
    z.literal("FAST_UW"),
    z.literal("DOC_PROCESS"),
    z.literal("MANUAL"),
    z.literal("SUBMIT"),
    z.literal("COVERAGE_CHECK"),
]);

export type ServiceErrorResponse = z.infer<typeof ServiceErrorResponse>;
export const ServiceErrorResponse = z.object({
    status: z.number(),
    title: z.string(),
    detail: z.union([z.string(), z.undefined()]).optional(),
});

export type GenderEnum = z.infer<typeof GenderEnum>;
export const GenderEnum = z.union([z.literal("M"), z.literal("F")]);

export type AllowedStates = z.infer<typeof AllowedStates>;
export const AllowedStates = z.union([
    z.literal("AL"),
    z.literal("AK"),
    z.literal("AZ"),
    z.literal("AR"),
    z.literal("CA"),
    z.literal("CO"),
    z.literal("CT"),
    z.literal("DE"),
    z.literal("DC"),
    z.literal("FL"),
    z.literal("GA"),
    z.literal("HI"),
    z.literal("ID"),
    z.literal("IL"),
    z.literal("IN"),
    z.literal("IA"),
    z.literal("KS"),
    z.literal("KY"),
    z.literal("LA"),
    z.literal("ME"),
    z.literal("MD"),
    z.literal("MA"),
    z.literal("MI"),
    z.literal("MN"),
    z.literal("MS"),
    z.literal("MO"),
    z.literal("MT"),
    z.literal("NE"),
    z.literal("NV"),
    z.literal("NH"),
    z.literal("NJ"),
    z.literal("NM"),
    z.literal("NY"),
    z.literal("NC"),
    z.literal("ND"),
    z.literal("OH"),
    z.literal("OK"),
    z.literal("OR"),
    z.literal("PA"),
    z.literal("RI"),
    z.literal("SC"),
    z.literal("SD"),
    z.literal("TN"),
    z.literal("TX"),
    z.literal("UT"),
    z.literal("VT"),
    z.literal("VA"),
    z.literal("WA"),
    z.literal("WV"),
    z.literal("WI"),
    z.literal("WY"),
]);

export type AutoRecoveryRequest = z.infer<typeof AutoRecoveryRequest>;
export const AutoRecoveryRequest = z.object({
    daysBack: z.number(),
    productCodes: z.array(z.union([z.literal("MLTA"), z.literal("DMTERM"), z.literal("GIWL")])),
    alertEmails: z.union([z.array(z.string()), z.undefined()]).optional(),
});

export type SecondaryAddressee = z.infer<typeof SecondaryAddressee>;
export const SecondaryAddressee = z.object({
    firstName: z.string(),
    lastName: z.string(),
    middleInitial: z.union([z.string(), z.undefined()]).optional(),
    suffix: z
        .union([
            z.literal("Jr"),
            z.literal("Sr"),
            z.literal("II"),
            z.literal("III"),
            z.literal("IV"),
            z.literal("PhD"),
            z.literal("MD"),
            z.literal("ESQ"),
            z.literal("DDS"),
            z.literal("V"),
            z.literal("VI"),
            z.literal("VII"),
            z.literal("VIII"),
            z.literal("DVM"),
            z.literal("1st"),
            z.literal("2nd"),
            z.literal("3rd"),
            z.literal("4th"),
            z.literal("5th"),
            z.literal("6th"),
            z.literal("7th"),
            z.literal("8th"),
            z.literal("9th"),
            z.literal("10th"),
            z.literal("11th"),
            z.literal("12th"),
            z.literal("13th"),
            z.literal("14th"),
            z.literal("15th"),
            z.undefined(),
        ])
        .optional(),
    phone: z.union([z.string(), z.undefined()]).optional(),
    address: z.object({
        addressLine1: z.string(),
        addressLine2: z.union([z.string(), z.undefined()]).optional(),
        addressLine3: z.union([z.string(), z.undefined()]).optional(),
        city: z.string(),
        state: z.union([
            z.literal("AL"),
            z.literal("AK"),
            z.literal("AZ"),
            z.literal("AR"),
            z.literal("CA"),
            z.literal("CO"),
            z.literal("CT"),
            z.literal("DE"),
            z.literal("DC"),
            z.literal("FL"),
            z.literal("GA"),
            z.literal("HI"),
            z.literal("ID"),
            z.literal("IL"),
            z.literal("IN"),
            z.literal("IA"),
            z.literal("KS"),
            z.literal("KY"),
            z.literal("LA"),
            z.literal("ME"),
            z.literal("MD"),
            z.literal("MA"),
            z.literal("MI"),
            z.literal("MN"),
            z.literal("MS"),
            z.literal("MO"),
            z.literal("MT"),
            z.literal("NE"),
            z.literal("NV"),
            z.literal("NH"),
            z.literal("NJ"),
            z.literal("NM"),
            z.literal("NY"),
            z.literal("NC"),
            z.literal("ND"),
            z.literal("OH"),
            z.literal("OK"),
            z.literal("OR"),
            z.literal("PA"),
            z.literal("RI"),
            z.literal("SC"),
            z.literal("SD"),
            z.literal("TN"),
            z.literal("TX"),
            z.literal("UT"),
            z.literal("VT"),
            z.literal("VA"),
            z.literal("WA"),
            z.literal("WV"),
            z.literal("WI"),
            z.literal("WY"),
        ]),
        zipCode: z.string(),
        country: z.union([z.string(), z.undefined()]).optional(),
    }),
});

export type FindOfferRequest = z.infer<typeof FindOfferRequest>;
export const FindOfferRequest = z.object({
    finderNumber: z.string().optional(),
    purl: z.string().optional(),
});

export type GetQuoteSuccessResponse = z.infer<typeof GetQuoteSuccessResponse>;
export const GetQuoteSuccessResponse = z.object({
    finderNumber: z.string().optional(),
    dateOfBirth: z.array(z.number()).optional(),
    state: z.string().optional(),
    gender: z.string().optional(),
    hasUsedNicotine: z.boolean().optional(),
    coverageAmount: z.number().optional(),
    coveragePlan: z.string().optional(),
    includeFamilyCoverage: z.boolean().optional(),
    premium: z.number().optional(),
});

/**
 * DTO Application storage request schema
 * This schema matches ALL fields from createStoreAppRequest in useContactInformation.ts
 * With validation rules matching the backend OpenAPI specification
 */
export type StoreDTORequest = z.infer<typeof StoreDTORequest>;
export const StoreDTORequest = z.object({
    // Application identifier
    finderNum: z
        .string()
        .min(10)
        .max(10)
        .regex(/^[GKMZ][a-zA-Z0-9]{9}$/, "Finder number must start with G, K, M, or Z followed by 9 alphanumeric characters"),

    // Applicant information
    gender: GenderEnum.optional(),
    firstName: z
        .string()
        .min(1)
        .max(20)
        .regex(
            /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,19}$/,
            "First name must start with alphanumeric or -'. and contain only valid characters"
        )
        .optional(),
    lastName: z
        .string()
        .min(1)
        .max(29)
        .regex(
            /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,28}$/,
            "Last name must start with alphanumeric or -'. and contain only valid characters"
        )
        .optional(),
    email: z.string().email().max(100).optional(),
    phone: z
        .string()
        .min(10)
        .max(10)
        .regex(/^[2-9]{1}[0-9]{2}[2-9]{1}[0-9]{6}$/, "Phone number must be 10 digits in valid US format")
        .optional(),
    phoneType: z.enum(["Home", "Business", "Mobile"]).optional(),
    tcpaConsent: z.boolean().optional(),

    // Applicant address
    addressline1: z
        .string()
        .min(1)
        .max(30)
        .regex(/^[A-Za-z0-9\-'. /#%@]{1,30}$/, "Address must contain only valid characters")
        .optional(),
    city: z
        .string()
        .min(1)
        .max(20)
        .regex(
            /^[a-zA-Z0-9'.\-\s]{1,20}$/,
            "City must contain only letters, numbers, apostrophes, periods, hyphens, and spaces"
        )
        .optional(),
    zipCd: z
        .string()
        .min(5)
        .max(5)
        .regex(/^\d{5}$/, "ZIP code must be exactly 5 digits")
        .optional(),

    // Statement of Health
    heightFeet: z
        .string()
        .max(1)
        .regex(/^[3-7]$/, "Height in feet must be between 3 and 7")
        .optional(),
    heightInches: z
        .string()
        .max(2)
        .regex(/^(0?[0-9]|1[01])$/, "Height in inches must be between 0 and 11")
        .optional(),
    weight: z
        .string()
        .max(3)
        .regex(/^\d{1,3}$/, "Weight must be 1 to 3 digits")
        .optional(),
    hasMedicalConditions: z.boolean().optional(),
    hasDiagnosticTesting: z.boolean().optional(),

    // Coverage Replacement
    replacementIndicator: z.boolean().optional(),
    replacementCompany: z.string().max(100).optional(),
    replacementAccount: z.string().max(50).optional(),

    // Beneficiary information
    beneFirstName: z
        .string()
        .min(1)
        .max(20)
        .regex(
            /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,19}$/,
            "Beneficiary first name must start with alphanumeric or -'. and contain only valid characters"
        )
        .optional(),
    beneLastName: z
        .string()
        .min(1)
        .max(29)
        .regex(
            /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,28}$/,
            "Beneficiary last name must start with alphanumeric or -'. and contain only valid characters"
        )
        .optional(),
    beneRelationship: z.union([BeneficiaryRelationshipsEnumSchema, z.undefined()]).optional(),

    // Secondary Addressee
    hasSecondaryAddressee: z.boolean().optional(),
    secondaryFirstName: z
        .string()
        .min(1)
        .max(20)
        .regex(
            /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,19}$/,
            "Secondary first name must start with alphanumeric or -'. and contain only valid characters"
        )
        .optional(),
    secondaryLastName: z
        .string()
        .min(1)
        .max(29)
        .regex(
            /^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,28}$/,
            "Secondary last name must start with alphanumeric or -'. and contain only valid characters"
        )
        .optional(),
    secondaryAddress: z
        .string()
        .min(1)
        .max(30)
        .regex(/^[A-Za-z0-9\-'. /#%@]{1,30}$/, "Secondary address must contain only valid characters")
        .optional(),
    secondaryCity: z
        .string()
        .min(1)
        .max(20)
        .regex(
            /^[a-zA-Z0-9'.\-\s]{1,20}$/,
            "Secondary city must contain only letters, numbers, apostrophes, periods, hyphens, and spaces"
        )
        .optional(),
    secondaryState: z.union([AllowedStates, z.undefined()]).optional(),
    secondaryZipCd: z
        .string()
        .min(5)
        .max(5)
        .regex(/^\d{5}$/, "Secondary ZIP code must be exactly 5 digits")
        .optional(),
    secondaryPhone: z
        .string()
        .min(10)
        .max(10)
        .regex(/^[2-9]{1}[0-9]{2}[2-9]{1}[0-9]{6}$/, "Secondary phone number must be 10 digits in valid US format")
        .optional(),

    // Identity check results
    policyNumber: z.string().max(10).optional(),
    hannoverId: z.string().max(50).optional(),

    // Payment
    paymentToken: z.string().max(300).optional(),
});

// ========== Generic persistence schemas (v2 endpoints) ==========

/**
 * Validates either a DTO/MLTA finder number (^[GKMZ][a-zA-Z0-9]{9}$)
 * or a GIWL shortened sessionID in hex 8-4-4-4-6 form (30 chars).
 */
export const UserRefSchema = z
    .string()
    .regex(/^([GKMZ][a-zA-Z0-9]{9}|[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{6})$/);
export type UserRef = z.infer<typeof UserRefSchema>;

export const ProductCodeSchema = z.union([z.literal("DMTERM"), z.literal("MLTA"), z.literal("GIWL")]);
export type ProductCode = z.infer<typeof ProductCodeSchema>;

export type StoreSessionIdRequest = z.infer<typeof StoreSessionIdRequest>;
export const StoreSessionIdRequest = z.object({
    userRef: UserRefSchema,
    saveContId: z.string().min(30).max(30),
    userIntent: z.string().optional(),
    dropOffPoint: z.string().optional(),
});

export type StoreQuoteRequest = z.infer<typeof StoreQuoteRequest>;
export const StoreQuoteRequest = z.object({
    userRef: UserRefSchema,
    productCode: ProductCodeSchema.optional(),
    dateOfBirth: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be in YYYY-MM-DD format")
        .optional(),
    state: AllowedStates.optional(),
    gender: GenderEnum.optional(),
    hasUsedNicotine: z.boolean().optional(),
    coverageAmount: z.number().int().min(0).optional(),
    coveragePlan: z.string().max(50).optional(),
    includeFamilyCoverage: z.boolean().optional(),
    premium: z.number().optional(),
});

export type StoreApplicationRequest = z.infer<typeof StoreApplicationRequest>;
export const StoreApplicationRequest = z.object({
    userRef: UserRefSchema,
    productCode: ProductCodeSchema.optional(),
    gender: GenderEnum.optional(),
    firstName: z
        .string()
        .min(1)
        .max(20)
        .regex(/^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,19}$/)
        .optional(),
    lastName: z
        .string()
        .min(1)
        .max(29)
        .regex(/^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,28}$/)
        .optional(),
    email: z.string().email().max(100).optional(),
    phone: z
        .string()
        .min(10)
        .max(10)
        .regex(/^[2-9]{1}[0-9]{2}[2-9]{1}[0-9]{6}$/)
        .optional(),
    phoneType: z.enum(["Home", "Business", "Mobile"]).optional(),
    tcpaConsent: z.boolean().optional(),
    addressline1: z
        .string()
        .min(1)
        .max(30)
        .regex(/^[A-Za-z0-9\-'. /#%@]{1,30}$/)
        .optional(),
    city: z
        .string()
        .min(1)
        .max(20)
        .regex(/^[a-zA-Z0-9'.\-\s]{1,20}$/)
        .optional(),
    zipCd: z
        .string()
        .min(5)
        .max(5)
        .regex(/^\d{5}$/)
        .optional(),
    aaaMembershipNumber: z
        .string()
        .regex(/^[0-9]{16}$/)
        .optional(),
    heightFeet: z
        .string()
        .max(1)
        .regex(/^[3-7]$/)
        .optional(),
    heightInches: z
        .string()
        .max(2)
        .regex(/^(0?[0-9]|1[01])$/)
        .optional(),
    weight: z
        .string()
        .max(3)
        .regex(/^\d{1,3}$/)
        .optional(),
    hasMedicalConditions: z.boolean().optional(),
    hasDiagnosticTesting: z.boolean().optional(),
    replacementIndicator: z.boolean().optional(),
    replacementCompany: z.string().max(100).optional(),
    replacementAccount: z.string().max(50).optional(),
    beneFirstName: z
        .string()
        .min(1)
        .max(20)
        .regex(/^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,19}$/)
        .optional(),
    beneLastName: z
        .string()
        .min(1)
        .max(29)
        .regex(/^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,28}$/)
        .optional(),
    beneRelationship: z.union([BeneficiaryRelationshipsEnumSchema, z.undefined()]).optional(),
    hasSecondaryAddressee: z.boolean().optional(),
    secondaryFirstName: z
        .string()
        .min(1)
        .max(20)
        .regex(/^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,19}$/)
        .optional(),
    secondaryLastName: z
        .string()
        .min(1)
        .max(29)
        .regex(/^[A-Za-z0-9\-'.][A-Za-z0-9\-'. ]{0,28}$/)
        .optional(),
    secondaryAddress: z
        .string()
        .min(1)
        .max(30)
        .regex(/^[A-Za-z0-9\-'. /#%@]{1,30}$/)
        .optional(),
    secondaryCity: z
        .string()
        .min(1)
        .max(20)
        .regex(/^[a-zA-Z0-9'.\-\s]{1,20}$/)
        .optional(),
    secondaryState: z.union([AllowedStates, z.undefined()]).optional(),
    secondaryZipCd: z
        .string()
        .min(5)
        .max(5)
        .regex(/^\d{5}$/)
        .optional(),
    secondaryPhone: z
        .string()
        .min(10)
        .max(10)
        .regex(/^[2-9]{1}[0-9]{2}[2-9]{1}[0-9]{6}$/)
        .optional(),
    policyNumber: z.string().max(10).optional(),
    hannoverId: z.string().max(50).optional(),
    paymentToken: z.string().max(300).optional(),
});

export type GetQuoteResponse = z.infer<typeof GetQuoteResponse>;
export const GetQuoteResponse = z.object({
    userRef: z.string().optional(),
    productCode: z.string().optional(),
    lastUpdated: z.number().optional(),
    dateOfBirth: z.array(z.number()).optional(),
    state: z.string().optional(),
    gender: z.string().optional(),
    hasUsedNicotine: z.boolean().optional(),
    coverageAmount: z.number().optional(),
    coveragePlan: z.string().optional(),
    includeFamilyCoverage: z.boolean().optional(),
    premium: z.number().optional(),
});

export type GetApplicationResponse = z.infer<typeof GetApplicationResponse>;
export const GetApplicationResponse = z.object({
    userRef: z.string().optional(),
    productCode: z.string().optional(),
    gender: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    phoneType: z.enum(["Home", "Business", "Mobile"]).optional(),
    tcpaConsent: z.boolean().optional(),
    zipCd: z.string().optional(),
    city: z.string().optional(),
    addressline1: z.string().optional(),
    aaaMembershipNumber: z
        .string()
        .regex(/^[0-9]{16}$/)
        .optional(),
    heightFeet: z.string().optional(),
    heightInches: z.string().optional(),
    weight: z.string().optional(),
    hasMedicalConditions: z.boolean().optional(),
    hasDiagnosticTesting: z.boolean().optional(),
    replacementIndicator: z.boolean().optional(),
    replacementCompany: z.string().optional(),
    replacementAccount: z.string().optional(),
    beneFirstName: z.string().optional(),
    beneLastName: z.string().optional(),
    beneRelationship: z.union([BeneficiaryRelationshipsEnumSchema, z.undefined()]).optional(),
    hasSecondaryAddressee: z.boolean().optional(),
    secondaryFirstName: z.string().optional(),
    secondaryLastName: z.string().optional(),
    secondaryAddress: z.string().optional(),
    secondaryCity: z.string().optional(),
    secondaryState: AllowedStates.optional(),
    secondaryZipCd: z.string().optional(),
    secondaryPhone: z.string().optional(),
    policyNumber: z.string().optional(),
    hannoverId: z.string().optional(),
    paymentToken: z.string().optional(),
});

export type Property = z.infer<typeof Property>;
export const Property = z.object({
    key: z.string().optional(),
    value: z.string().optional(),
});

export type Beneficiary = z.infer<typeof Beneficiary>;
export const Beneficiary = z.object({
    firstName: z.string(),
    middleInitial: z.union([z.string(), z.undefined()]).optional(),
    lastName: z.string(),
    percentage: z.number(),
    relationship: z.union([
        z.literal("Aunt"),
        z.literal("BusinessAssociate"),
        z.literal("BusinessPartner"),
        z.literal("Child"),
        z.literal("Cousin"),
        z.literal("DomesticPartner"),
        z.literal("Father"),
        z.literal("Fiancee"),
        z.literal("FormerSpouse"),
        z.literal("Friend"),
        z.literal("Grandchild"),
        z.literal("Mother"),
        z.literal("Nephew"),
        z.literal("Niece"),
        z.literal("NonSpouse"),
        z.literal("Sibling"),
        z.literal("Spouse"),
        z.literal("Uncle"),
    ]),
    beneficiaryType: z.union([z.literal("primary"), z.literal("contingent")]),
});

export type FindOfferSuccessResponse = z.infer<typeof FindOfferSuccessResponse>;
export const FindOfferSuccessResponse = z.object({
    planCode: z.union([z.string(), z.undefined()]).optional(),
    offer: z.union([z.string(), z.undefined()]).optional(),
    firstName: z.union([z.string(), z.undefined()]).optional(),
    lastName: z.union([z.string(), z.undefined()]).optional(),
    memberOfferAvailable: z.boolean(),
    spouseOfferAvailable: z.boolean(),
    formNumber: z.string(),
    keycode: z.union([z.string(), z.undefined()]).optional(),
    aaaMemberNumber: z.union([z.string(), z.undefined()]).optional(),
    memberLoyaltyYears: z.union([z.number(), z.undefined()]).optional(),
    memberJoinDate: z.union([z.string(), z.null(), z.undefined()]).optional(),
    gender: z.union([GenderEnum, z.undefined()]).optional(),
    finderNumber: z.union([z.string(), z.undefined()]).optional(),
    campaignCode: z.string(),
    address: z.union([z.string(), z.undefined()]).optional(),
    state: z.union([AllowedStates, z.undefined()]).optional(),
    city: z.union([z.string(), z.undefined()]).optional(),
    zipCd: z.union([z.string(), z.undefined()]).optional(),
});

export type Application = z.infer<typeof Application>;
export const Application = z.object({
    signedTimestamp: z.string(),
    applicationSubmissionTimestamp: z.string(),
    faceAmount: z.number(),
    applicationID: z.union([z.string(), z.undefined()]).optional(),
    policyNumber: z.string(),
    hannoverID: z.string(),
    paymentToken: z.string(),
    paymentType: z.union([z.literal("CC"), z.literal("ET"), z.undefined()]).optional(),
    lastFourDigits: z.union([z.string().regex(/^[0-9]{4}$/), z.undefined()]).optional(),
    modalPremiumAmount: z.number(),
    acceptanceConfirmed: z.boolean(),
    replacementIndicator: z.boolean(),
    replacementCompany: z.union([z.string(), z.undefined()]).optional(),
    replacementAccount: z.union([z.string(), z.undefined()]).optional(),
    offerPurl: z.union([z.string(), z.undefined()]).optional(),
    termPeriod: z.union([z.number(), z.undefined()]).optional(),
    applicant: z.object({
        firstName: z.string(),
        middleInitial: z.union([z.string(), z.undefined()]).optional(),
        lastName: z.string(),
        birthDate: z.string(),
        phone: z.string(),
        phoneType: z.union([z.literal("Home"), z.literal("Business"), z.literal("Business Fax"), z.literal("Mobile")]),
        usCitizen: z.union([z.boolean(), z.undefined()]).optional(),
        ssn: z.union([z.string(), z.undefined()]).optional(),
        address: z.object({
            addressLine1: z.string(),
            addressLine2: z.union([z.string(), z.undefined()]).optional(),
            addressLine3: z.union([z.string(), z.undefined()]).optional(),
            city: z.string(),
            state: AllowedStates,
            zipCode: z.string(),
            country: z.string(),
        }),
        weight: z.number(),
        gender: GenderEnum,
        aaaMember: z.boolean(),
        aaaMemberNumber: z.union([z.string(), z.undefined()]).optional(),
        formNumber: z.union([z.string(), z.undefined()]).optional(),
        keyCode: z.string(),
        email: z.string(),
        height: z.number(),
        nicotineUse: z.boolean(),
        treatment: z.boolean(),
        diagnosis: z.boolean(),
        employments: z.union([z.array(z.string()), z.undefined()]).optional(),
    }),
    hasSecondaryAddressee: z.boolean(),
    secondaryAddressee: z.union([SecondaryAddressee, z.undefined()]).optional(),
    beneficiaries: z.array(Beneficiary),
    additionalData: z.union([z.array(Property), z.undefined()]).optional(),
    campaign: z.object({
        campaignType: z.string(),
        productCode: z.string(),
        clubCode: z.string(),
        leadSource: z.union([z.string(), z.undefined()]).optional(),
        campaignName: z.string(),
        campaignCode: z.string(),
        offer: z.array(z.number()),
    }),
});

export type CoverageType = z.infer<typeof CoverageType>;
export const CoverageType = z.union([z.literal("Family"), z.literal("Individual")]);

export type DirectTermIdentityResponse = z.infer<typeof DirectTermIdentityResponse>;
export const DirectTermIdentityResponse = z.object({
    policyNumber: z.string(),
    hannoverId: z.string(),
    identityCheckStatus: z.union([z.literal("approved"), z.literal("declined")]),
});

export type DirectTermSubmissionResponse = z.infer<typeof DirectTermSubmissionResponse>;
export const DirectTermSubmissionResponse = z.object({
    policyStatus: z.union([
        z.literal("approved"),
        z.literal("declined"),
        z.literal("refertounderwriter"),
        z.literal("alreadyused"),
    ]),
});

export type GIWL_Application = z.infer<typeof GIWL_Application>;
export const GIWL_Application = z.object({
    objectType: z.union([z.string(), z.undefined()]).optional(),
    signedTimestamp: z.string(),
    applicationSubmissionTimestamp: z.string(),
    faceAmount: z.number(),
    // policyNumber is required: it is minted up front via POST /generateFastPolicyNumber during
    // contact submission and carried on the request. applicationID is backend-internal and stays
    // optional. See the GIWL submit flow note in transformGIWLSubmissionData.
    applicationID: z
        .union([
            z
                .string()
                .min(1)
                .max(10)
                .regex(/^[a-zA-Z0-9\-]{10}$/),
            z.undefined(),
        ])
        .optional(),
    policyNumber: z
        .string()
        .min(10)
        .max(10)
        .regex(/^\d{10}$/),
    isOverCovered: z.union([z.boolean(), z.undefined()]).optional(),
    isInstantIdCheckSuccessful: z.union([z.boolean(), z.undefined()]).optional(),
    offerPurl: z.union([z.string(), z.undefined()]).optional(),
    paymentToken: z.string(),
    paymentType: z.union([z.literal("CC"), z.literal("ET")]),
    lastFourDigits: z.string().regex(/^[0-9]{4}$/),
    modalPremiumAmount: z.number(),
    applicant: z.object({
        firstName: z.string(),
        middleInitial: z.union([z.string(), z.undefined()]).optional(),
        email: z.union([z.string(), z.undefined()]).optional(),
        lastName: z.string(),
        suffix: z
            .union([
                z.literal("Jr"),
                z.literal("Sr"),
                z.literal("II"),
                z.literal("III"),
                z.literal("IV"),
                z.literal("PhD"),
                z.literal("MD"),
                z.literal("ESQ"),
                z.literal("DDS"),
                z.literal("V"),
                z.literal("VI"),
                z.literal("VII"),
                z.literal("VIII"),
                z.literal("DVM"),
                z.literal("1st"),
                z.literal("2nd"),
                z.literal("3rd"),
                z.literal("4th"),
                z.literal("5th"),
                z.literal("6th"),
                z.literal("7th"),
                z.literal("8th"),
                z.literal("9th"),
                z.literal("10th"),
                z.literal("11th"),
                z.literal("12th"),
                z.literal("13th"),
                z.literal("14th"),
                z.literal("15th"),
                z.undefined(),
            ])
            .optional(),
        birthDate: z.string(),
        phone: z.string(),
        phoneType: z.union([z.literal("Home"), z.literal("Business"), z.literal("Business Fax"), z.literal("Mobile")]),
        aaaMember: z.boolean(),
        aaaMemberNumber: z.union([z.string(), z.undefined()]).optional(),
        address: z.object({
            addressLine1: z.string(),
            addressLine2: z.union([z.string(), z.undefined()]).optional(),
            addressLine3: z.union([z.string(), z.undefined()]).optional(),
            city: z.string(),
            state: AllowedStates,
            zipCode: z.string(),
            country: z.string(),
        }),
        gender: GenderEnum,
        keyCode: z.string().min(10).max(10),
    }),
    hasSecondaryAddressee: z.boolean(),
    secondaryAddressee: z.union([SecondaryAddressee, z.undefined()]).optional(),
    replacementIndicator: z.boolean(),
    replacementCompany: z.union([z.string(), z.undefined()]).optional(),
    replacementAccount: z.union([z.string(), z.undefined()]).optional(),
    beneficiaries: z.array(Beneficiary),
    additionalData: z.union([z.array(Property), z.undefined()]).optional(),
    campaign: z.object({
        campaignType: z.string(),
        productCode: z.literal("GIWL"),
        clubCode: z
            .string()
            .min(3)
            .max(3)
            .regex(/^[A-Z0-9]{3}$/),
        leadSource: z
            .union([
                z
                    .string()
                    .min(1)
                    .max(3)
                    .regex(/^[A-Z0-9]{1,3}$/),
                z.undefined(),
            ])
            .optional(),
        campaignName: z.string(),
        campaignCode: z.string().min(1).max(10),
    }),
});

export type GIWL_SubmissionResponse = z.infer<typeof GIWL_SubmissionResponse>;
export const GIWL_SubmissionResponse = z.object({
    policyStatus: z.union([z.literal("approved"), z.literal("declined"), z.literal("incomplete"), z.literal("exception")]),
    policyNumber: z.union([z.string(), z.undefined()]).optional(),
});

export type Tiers = z.infer<typeof Tiers>;
export const Tiers = z.union([
    z.literal("Value"),
    z.literal("Economy"),
    z.literal("Deluxe"),
    z.literal("Ultra"),
    z.literal("Superior"),
    z.literal("Premier"),
]);

export type MLTA_Application = z.infer<typeof MLTA_Application>;
export const MLTA_Application = z.object({
    objectType: z.union([z.string(), z.undefined()]).optional(),
    signedTimestamp: z.string(),
    applicationSubmissionTimestamp: z.string(),
    faceAmount: z.number(),
    applicationID: z.union([z.string(), z.undefined()]).optional(),
    policyNumber: z.string(),
    paymentToken: z.string(),
    paymentType: z.union([z.literal("CC"), z.literal("ET"), z.undefined()]).optional(),
    lastFourDigits: z.union([z.string().regex(/^[0-9]{4}$/), z.undefined()]).optional(),
    modalPremiumAmount: z.number(),
    acceptanceConfirmed: z.boolean(),
    offerPurl: z.union([z.string(), z.undefined()]).optional(),
    tiers: z.array(Tiers),
    tierSelection: Tiers,
    coverageTypeSelection: CoverageType,
    applicant: z.object({
        firstName: z.string(),
        middleInitial: z.union([z.string(), z.undefined()]).optional(),
        lastName: z.string(),
        birthDate: z.string(),
        phone: z.string(),
        phoneType: z.union([z.literal("Home"), z.literal("Business"), z.literal("Business Fax"), z.literal("Mobile")]),
        usCitizen: z.union([z.boolean(), z.undefined()]).optional(),
        ssn: z.union([z.string(), z.undefined()]).optional(),
        address: z.object({
            addressLine1: z.string(),
            addressLine2: z.union([z.string(), z.undefined()]).optional(),
            addressLine3: z.union([z.string(), z.undefined()]).optional(),
            city: z.string(),
            state: AllowedStates,
            zipCode: z.string(),
            country: z.string(),
        }),
        gender: GenderEnum,
        aaaMemberNumber: z.string(),
        memberJoinDate: z.union([z.string(), z.null(), z.undefined()]).optional(),
        membershipLevel: z.number(),
        formNumber: z.union([z.string(), z.undefined()]).optional(),
        keyCode: z.string(),
        email: z.string(),
        employments: z.union([z.array(z.string()), z.undefined()]).optional(),
    }),
    hasSecondaryAddressee: z.boolean(),
    secondaryAddressee: z.union([SecondaryAddressee, z.undefined()]).optional(),
    beneficiaries: z.array(Beneficiary),
    additionalData: z.union([z.array(Property), z.undefined()]).optional(),
    campaign: z.object({
        campaignType: z.string(),
        productCode: z.union([z.literal("MLTA"), z.literal("ACLTA")]),
        clubCode: z.string(),
        leadSource: z.union([z.string(), z.undefined()]).optional(),
        campaignName: z.string(),
        campaignCode: z.string(),
    }),
});

export type FastPolicyNumber = z.infer<typeof FastPolicyNumber>;
export const FastPolicyNumber = z.object({
    policyNumber: z.string().optional(),
});

export type MLTA_SubmissionResponse = z.infer<typeof MLTA_SubmissionResponse>;
export const MLTA_SubmissionResponse = z.object({
    policyStatus: z.union([z.literal("approved"), z.literal("alreadyused")]),
});

export type Person = z.infer<typeof Person>;
export const Person = z.object({
    firstName: z.string(),
    middleInitial: z.union([z.string(), z.undefined()]).optional(),
    lastName: z.string(),
    birthDate: z.string(),
    phone: z.string(),
    ssn: z.union([z.string(), z.undefined()]).optional(),
    policyNumber: z.union([z.string(), z.undefined()]).optional(),
    hannoverID: z.union([z.string(), z.undefined()]).optional(),
    address: z.object({
        addressLine1: z.string(),
        addressLine2: z.union([z.string(), z.undefined()]).optional(),
        addressLine3: z.union([z.string(), z.undefined()]).optional(),
        city: z.string(),
        state: AllowedStates,
        zipCode: z.string(),
        country: z.string(),
    }),
    gender: z.union([GenderEnum, z.string(), z.null()]),
    email: z.string(),
});

export type post_Submit = typeof post_Submit;
export const post_Submit = {
    method: z.literal("POST"),
    path: z.literal("/submit"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: Application,
    }),
    response: DirectTermSubmissionResponse,
};

export type post_SubmitMLTAApplication = typeof post_SubmitMLTAApplication;
export const post_SubmitMLTAApplication = {
    method: z.literal("POST"),
    path: z.literal("/submitMLTAApplication"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: MLTA_Application,
    }),
    response: MLTA_SubmissionResponse,
};

export type post_SubmitGIWLApplication = typeof post_SubmitGIWLApplication;
export const post_SubmitGIWLApplication = {
    method: z.literal("POST"),
    path: z.literal("/submitGIWLApplication"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: GIWL_Application,
    }),
    response: GIWL_SubmissionResponse,
};

export type post_Recover = typeof post_Recover;
export const post_Recover = {
    method: z.literal("POST"),
    path: z.literal("/recover"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
            recoveryStep: z.union([
                z.literal("FETCH_PAYMENT"),
                z.literal("FAST_SUBMIT"),
                z.literal("FAST_UW"),
                z.literal("DOC_PROCESS"),
                z.literal("MANUAL"),
                z.literal("SUBMIT"),
                z.literal("COVERAGE_CHECK"),
                z.undefined(),
            ]),
        }),
        body: Application,
    }),
    response: DirectTermSubmissionResponse,
};

export type post_Recovermlta = typeof post_Recovermlta;
export const post_Recovermlta = {
    method: z.literal("POST"),
    path: z.literal("/recovermlta"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
            recoveryStep: z.union([
                z.literal("FETCH_PAYMENT"),
                z.literal("FAST_SUBMIT"),
                z.literal("FAST_UW"),
                z.literal("DOC_PROCESS"),
                z.literal("MANUAL"),
                z.literal("SUBMIT"),
                z.literal("COVERAGE_CHECK"),
                z.undefined(),
            ]),
        }),
        body: MLTA_Application,
    }),
    response: MLTA_SubmissionResponse,
};

export type post_AutoRecovery = typeof post_AutoRecovery;
export const post_AutoRecovery = {
    method: z.literal("POST"),
    path: z.literal("/autoRecovery"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        body: AutoRecoveryRequest,
    }),
    response: z.boolean(),
};

export type post_CheckIdentity = typeof post_CheckIdentity;
export const post_CheckIdentity = {
    method: z.literal("POST"),
    path: z.literal("/checkIdentity"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: Person,
    }),
    response: DirectTermIdentityResponse,
};

export type post_FindOffer = typeof post_FindOffer;
export const post_FindOffer = {
    method: z.literal("POST"),
    path: z.literal("/findOffer"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: FindOfferRequest,
    }),
    response: FindOfferSuccessResponse,
};

// ========== Generic endpoint definitions (v2) ==========

export type put_StoreSessionId = typeof put_StoreSessionId;
export const put_StoreSessionId = {
    method: z.literal("PUT"),
    path: z.literal("/storeSessionId"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: StoreSessionIdRequest,
    }),
};

export type put_StoreQuote = typeof put_StoreQuote;
export const put_StoreQuote = {
    method: z.literal("PUT"),
    path: z.literal("/storeQuote"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: StoreQuoteRequest,
    }),
};

export type put_StoreApplication = typeof put_StoreApplication;
export const put_StoreApplication = {
    method: z.literal("PUT"),
    path: z.literal("/storeApplication"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
        body: StoreApplicationRequest,
    }),
};

export type get_GetQuote = typeof get_GetQuote;
export const get_GetQuote = {
    method: z.literal("GET"),
    path: z.literal("/getQuote"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        query: z.object({
            userRef: z.string(),
        }),
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
    }),
    response: GetQuoteResponse,
};

export type get_GetApplication = typeof get_GetApplication;
export const get_GetApplication = {
    method: z.literal("GET"),
    path: z.literal("/getApplication"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        query: z.object({
            userRef: z.string(),
        }),
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
    }),
    response: GetApplicationResponse,
};

export type FeatureFlag = z.infer<typeof FeatureFlag>;
export const FeatureFlag = z.object({
    flagKey: z.string().optional(),
    flagName: z.string().optional(),
    description: z.string().optional(),
    isEnabled: z.boolean().optional(),
});

export type FeatureFlags = z.infer<typeof FeatureFlags>;
export const FeatureFlags = z.array(FeatureFlag);

export type get_GetAllFeatureFlags = typeof get_GetAllFeatureFlags;
export const get_GetAllFeatureFlags = {
    method: z.literal("GET"),
    path: z.literal("/getFeatureFlags"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
    }),
    response: FeatureFlags,
};

export type get_GetFeatureFlagByKey = typeof get_GetFeatureFlagByKey;
export const get_GetFeatureFlagByKey = {
    method: z.literal("GET"),
    path: z.literal("/getFeatureFlags/{flagKey}"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        path: z.object({
            flagKey: z.string(),
        }),
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
    }),
    response: FeatureFlag,
};

export type post_GenerateFastPolicyNumber = typeof post_GenerateFastPolicyNumber;
export const post_GenerateFastPolicyNumber = {
    method: z.literal("POST"),
    path: z.literal("/generateFastPolicyNumber"),
    requestFormat: z.literal("json"),
    parameters: z.object({
        header: z.object({
            trackingID: z.string(),
            channelID: z.literal("web"),
        }),
    }),
    response: FastPolicyNumber,
};

// <EndpointByMethod>
export const EndpointByMethod = {
    post: {
        "/submit": post_Submit,
        "/submitMLTAApplication": post_SubmitMLTAApplication,
        "/submitGIWLApplication": post_SubmitGIWLApplication,
        "/recover": post_Recover,
        "/recovermlta": post_Recovermlta,
        "/autoRecovery": post_AutoRecovery,
        "/checkIdentity": post_CheckIdentity,
        "/findOffer": post_FindOffer,
        "/generateFastPolicyNumber": post_GenerateFastPolicyNumber,
    },
    put: {},
    get: {
        "/getFeatureFlags": get_GetAllFeatureFlags,
        "/getFeatureFlags/{flagKey}": get_GetFeatureFlagByKey,
    },
};
export type EndpointByMethod = typeof EndpointByMethod;
// </EndpointByMethod>

// <EndpointByMethod.Shorthands>
export type PostEndpoints = EndpointByMethod["post"];
export type PutEndpoints = EndpointByMethod["put"];
export type GetEndpoints = EndpointByMethod["get"];
export type AllEndpoints = EndpointByMethod[keyof EndpointByMethod];
// </EndpointByMethod.Shorthands>

// <ApiClientTypes>
export type EndpointParameters = {
    body?: unknown;
    query?: Record<string, unknown>;
    header?: Record<string, unknown>;
    path?: Record<string, unknown>;
};

export type MutationMethod = "post" | "put" | "patch" | "delete";
export type Method = "get" | "head" | "options" | MutationMethod;

type RequestFormat = "json" | "form-data" | "form-url" | "binary" | "text";

export type DefaultEndpoint = {
    parameters?: EndpointParameters | undefined;
    response: unknown;
};

export type Endpoint<TConfig extends DefaultEndpoint = DefaultEndpoint> = {
    operationId: string;
    method: Method;
    path: string;
    requestFormat: RequestFormat;
    parameters?: TConfig["parameters"];
    meta: {
        alias: string;
        hasParameters: boolean;
        areParametersRequired: boolean;
    };
    response: TConfig["response"];
};

type Fetcher = (method: Method, url: string, parameters?: EndpointParameters | undefined) => Promise<Endpoint["response"]>;

type RequiredKeys<T> = {
    [P in keyof T]-?: undefined extends T[P] ? never : P;
}[keyof T];

type MaybeOptionalArg<T> = RequiredKeys<T> extends never ? [config?: T] : [config: T];

// </ApiClientTypes>

// <ApiClient>
export class ApiClient {
    baseUrl: string = "";

    constructor(public fetcher: Fetcher) {}

    setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
        return this;
    }

    // <ApiClient.post>
    post<Path extends keyof PostEndpoints, TEndpoint extends PostEndpoints[Path]>(
        path: Path,
        ...params: MaybeOptionalArg<z.infer<TEndpoint["parameters"]>>
    ): Promise<z.infer<TEndpoint["response"]>> {
        return this.fetcher("post", this.baseUrl + path, params[0]) as Promise<z.infer<TEndpoint["response"]>>;
    }
    // </ApiClient.post>
}

export function createApiClient(fetcher: Fetcher, baseUrl?: string) {
    return new ApiClient(fetcher).setBaseUrl(baseUrl ?? "");
}

/**
 Example usage:
 const api = createApiClient((method, url, params) =>
   fetch(url, { method, body: JSON.stringify(params) }).then((res) => res.json()),
 );
 api.get("/users").then((users) => console.log(users));
 api.post("/users", { body: { name: "John" } }).then((user) => console.log(user));
 api.put("/users/:id", { path: { id: 1 }, body: { name: "John" } }).then((user) => console.log(user));
*/

// </ApiClient
